(function () {
  if (typeof window === "undefined") return;
  if (window.__MEAMA_WV_OPTIMIZER_ACTIVE__) return;
  window.__MEAMA_WV_OPTIMIZER_ACTIVE__ = true;

  const managedStyleAttr = "data-wv-managed-style";
  const managedScriptAttr = "data-wv-managed-script";
  const cachePrefix = "meama_tab_cache_v2_";
  const cacheMetaKey = `${cachePrefix}__meta__`;
  const legacyCachePrefixes = ["meama_tab_cache_"];
  const cacheTtlMs = 10 * 60 * 1000;
  const maxCacheEntries = 8;
  const maxSnapshotBytes = 900 * 1024;

  const normalizePathname = (pathname) =>
    String(pathname || "").replace(/\/+$/, "") || "/";

  const stripLocalePrefix = (pathname) => {
    const normalized = normalizePathname(pathname);
    if (normalized === "/") return normalized;
    return normalized.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, "") || "/";
  };

  const toCanonicalPathname = (pathname) => stripLocalePrefix(pathname);

  const normalizeUrl = (input) => {
    try {
      let raw = input;
      if (typeof raw === "string" && /^shopify:\/\//i.test(raw)) {
        const relative = raw.replace(/^shopify:\/+/i, "");
        raw = `${location.origin}/${relative.replace(/^\/+/, "")}`;
      }

      const url = raw instanceof URL ? raw : new URL(raw, location.href);
      if (url.origin !== location.origin) return null;

      const pathname = normalizePathname(url.pathname);
      const canonicalPathname = toCanonicalPathname(pathname);
      const search = url.search || "";
      const originPath = `${url.origin}${pathname}`;
      const absolute = `${originPath}${search}`;

      return { url, pathname, canonicalPathname, search, originPath, absolute };
    } catch {
      return null;
    }
  };

  const getCurrentLocalePrefix = () => {
    const path = normalizePathname(location.pathname);
    if (path === "/") return "";
    const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
    return match ? `/${match[1]}` : "";
  };

  const isMeamaReactNativeWebView = (() => {
    const ua = navigator.userAgent || "";
    const hasBridge =
      !!window.ReactNativeWebView &&
      typeof window.ReactNativeWebView.postMessage === "function";
    const hasAppUa = /MeamaApp/i.test(ua);

    if (hasBridge || hasAppUa) return true;

    // Fallback only for actual in-app webviews to avoid running in normal browsers.
    const ios = /iPhone|iPod|iPad/i.test(ua);
    const safari = /Safari/i.test(ua);
    const standalone = window.navigator ? window.navigator.standalone : false;
    const androidWV = /\bwv\b|Android.*Version\/[\d.]+/i.test(ua);
    return (ios && (!safari || standalone === true)) || androidWV;
  })();

  if (!isMeamaReactNativeWebView) return;

  const runIdle = (fn, timeout = 400) =>
    "requestIdleCallback" in window
      ? requestIdleCallback(fn, { timeout })
      : setTimeout(fn, timeout);

  const blockedPathnames = new Set([]);

  const currentCanonicalPath = toCanonicalPathname(location.pathname);
  if (blockedPathnames.has(currentCanonicalPath)) {
    console.warn("[WV] Script aborted due to blocked path:", currentCanonicalPath);
    return;
  }

  // One-time soft reload to clear stale execution/caching issues after deploys.
  try {
    if (!sessionStorage.getItem("wv_refresh_once")) {
      sessionStorage.setItem("wv_refresh_once", "1");
      window.location.replace(window.location.href);
      return;
    }
  } catch {}

  const hardReloadPathnames = new Set([
    "/",
    "/pages/locations", // map screen
    "/account/login",
    "/account/register",
    "/account",
    "/partial-pay",
    "/pages/partial-pay",
    "/cart",
  ]);
  const isHardReloadPath = (pathname) =>
    hardReloadPathnames.has(toCanonicalPathname(pathname));

  const isSensitiveFlowPath = (pathname) =>
    /^(?:\/checkout\b|\/account\b|\/cart\b|\/apps?\b|\/a\/|\/challenge\b)/i.test(
      toCanonicalPathname(pathname)
    );

  let firstPaintGuardApplied = false;
  const firstPaintGuardId = "wv-first-paint-hide";

  const applyFirstPaintGuard = () => {
    if (firstPaintGuardApplied) return;
    firstPaintGuardApplied = true;
    try {
      const style = document.createElement("style");
      style.id = firstPaintGuardId;
      style.setAttribute(managedStyleAttr, "1");
      style.textContent = `
        #shopify-section-announcement-bar, .announcement-bar { display: block !important; visibility: visible !important; opacity: 1 !important; }
        header, header.header, header.site-header, #shopify-section-header, [data-section-type="header"],
        footer, .footer, #shopify-section-footer, [data-section-type="footer"] { display: none !important; }
      `;
      const anchor = document.head.querySelector("style, link[rel='stylesheet']");
      if (anchor) document.head.insertBefore(style, anchor);
      else document.head.appendChild(style);
    } catch {}
  };

  const releaseFirstPaintGuard = () => {
    try {
      const s = document.getElementById(firstPaintGuardId);
      if (s) s.remove();
    } catch {}
  };

  try {
    if (!sessionStorage.getItem("wv_first_paint_hidden")) {
      sessionStorage.setItem("wv_first_paint_hidden", "1");
      applyFirstPaintGuard();
      window.addEventListener(
        "load",
        () => runIdle(releaseFirstPaintGuard, 600),
        { once: true }
      );
    }
  } catch {}

  let dockLinks = [];
  const allowedCacheTargets = new Set();
  const warmPrefetchSeen = new Map();

  const getLocalizedRoute = (canonicalPath) => {
    const prefix = getCurrentLocalePrefix();
    if (!canonicalPath || canonicalPath === "/") return prefix || "/";
    return `${prefix}${canonicalPath}`.replace(/\/{2,}/g, "/");
  };

  const addAllowedTarget = (pathOrUrl) => {
    const normalized = normalizeUrl(pathOrUrl);
    if (!normalized) return;
    if (isHardReloadPath(normalized.pathname)) return;
    allowedCacheTargets.add(normalized.originPath);
  };

  const refreshCacheTargets = () => {
    allowedCacheTargets.clear();

    const dockAnchors = [...document.querySelectorAll("mobile-dock a[href]")];
    const optInAnchors = [...document.querySelectorAll("a[href][data-wv-cache]")];
    const anchors = [...new Set([...dockAnchors, ...optInAnchors])];
    dockLinks = dockAnchors;

    anchors.forEach((a) => addAllowedTarget(a.getAttribute("href")));

    // Explicitly include app tab routes even when dock links use "shopify://" placeholders.
    addAllowedTarget(getLocalizedRoute("/collections/all-products"));
    addAllowedTarget(getLocalizedRoute("/collections/offers"));
    addAllowedTarget(getLocalizedRoute("/"));
  };

  const getCacheKey = (abs) => cachePrefix + abs;

  const readCacheMeta = () => {
    try {
      const raw = localStorage.getItem(cacheMetaKey);
      const meta = raw ? JSON.parse(raw) : {};
      return meta && typeof meta === "object" ? meta : {};
    } catch {
      return {};
    }
  };

  const writeCacheMeta = (meta) => {
    try {
      localStorage.setItem(cacheMetaKey, JSON.stringify(meta));
    } catch {}
  };

  const pruneCacheStorage = ({ aggressive = false } = {}) => {
    try {
      const meta = readCacheMeta();
      const now = Date.now();
      const entries = [];
      const toRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(cachePrefix) || key === cacheMetaKey) continue;

        const record = meta[key];
        const ts = record && Number.isFinite(record.ts) ? record.ts : 0;
        const age = ts ? now - ts : Number.POSITIVE_INFINITY;

        if (aggressive || age > cacheTtlMs) {
          toRemove.push(key);
          delete meta[key];
          continue;
        }

        entries.push({ key, ts });
      }

      entries.sort((a, b) => b.ts - a.ts);
      entries.slice(maxCacheEntries).forEach((entry) => {
        toRemove.push(entry.key);
        delete meta[entry.key];
      });

      toRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {}
      });

      writeCacheMeta(meta);
    } catch {}
  };

  const touchCacheMeta = (key, bytes) => {
    try {
      const meta = readCacheMeta();
      meta[key] = { ts: Date.now(), bytes: bytes || 0 };
      writeCacheMeta(meta);
    } catch {}
  };

  const removeCacheEntry = (key) => {
    try {
      localStorage.removeItem(key);
    } catch {}
    try {
      const meta = readCacheMeta();
      if (meta[key]) {
        delete meta[key];
        writeCacheMeta(meta);
      }
    } catch {}
  };

  const readCachedSnapshot = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const meta = readCacheMeta()[key];
      if (meta && Number.isFinite(meta.ts) && Date.now() - meta.ts > cacheTtlMs) {
        removeCacheEntry(key);
        return null;
      }

      touchCacheMeta(key, raw.length);
      return raw;
    } catch {
      return null;
    }
  };

  const writeCachedSnapshot = (key, snapshot) => {
    if (!snapshot || snapshot.length > maxSnapshotBytes) return false;

    try {
      localStorage.setItem(key, snapshot);
      touchCacheMeta(key, snapshot.length);
      pruneCacheStorage();
      return true;
    } catch {
      try {
        pruneCacheStorage({ aggressive: true });
        localStorage.setItem(key, snapshot);
        touchCacheMeta(key, snapshot.length);
        pruneCacheStorage();
        return true;
      } catch {
        return false;
      }
    }
  };

  const createCacheSnapshot = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const main = doc.querySelector("main");
      if (!main) return null;

      const fragments = [];
      const title = doc.querySelector("title");
      if (title) fragments.push(title.outerHTML);

      doc.querySelectorAll("link[rel='stylesheet'],style").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      doc.querySelectorAll("head script[data-wv-exec]").forEach((el) => {
        fragments.push(el.outerHTML);
      });

      return `<html><head>${fragments.join("")}</head><body>${main.outerHTML}</body></html>`;
    } catch {
      return null;
    }
  };

  const purgeLegacyCache = () => {
    try {
      legacyCachePrefixes.forEach((prefix) => {
        if (!prefix || prefix === cachePrefix) return;
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (!key || !key.startsWith(prefix)) continue;
          try {
            localStorage.removeItem(key);
          } catch {}
        }
      });

      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(cachePrefix) || key === cacheMetaKey) continue;
        const absolute = key.slice(cachePrefix.length);
        try {
          const u = new URL(absolute);
          const originPath = `${u.origin}${normalizePathname(u.pathname)}`;
          if (!allowedCacheTargets.has(originPath)) toRemove.push(key);
        } catch {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => removeCacheEntry(k));
    } catch {}
  };

  const waitForStylesheet = (link) =>
    new Promise((resolve) => {
      const done = () => resolve();
      link.addEventListener("load", done, { once: true });
      link.addEventListener("error", done, { once: true });
      setTimeout(done, 1200);
    });

  const buildStyleKey = (node) => {
    if (node.tagName === "LINK") return `link:${node.href}`;
    if (node.tagName === "STYLE") return `style:${(node.textContent || "").trim()}`;
    return null;
  };

  const buildScriptKey = (node) =>
    node.src ? `src:${node.src}` : `inline:${(node.textContent || "").trim()}`;

  const copyAllAttributes = (from, to) => {
    try {
      [...from.attributes].forEach((attr) => to.setAttribute(attr.name, attr.value));
    } catch {}
  };

  const syncPageStyles = async (doc) => {
    const nonManagedKeys = new Set();
    document.head
      .querySelectorAll(`link[rel='stylesheet']:not([${managedStyleAttr}]), style:not([${managedStyleAttr}])`)
      .forEach((n) => {
        const key = buildStyleKey(n);
        if (key) nonManagedKeys.add(key);
      });

    const managedExisting = new Map();
    document.head.querySelectorAll(`[${managedStyleAttr}]`).forEach((n) => {
      const key = buildStyleKey(n);
      if (!key) return;

      if (nonManagedKeys.has(key)) {
        n.remove();
        return;
      }

      if (!managedExisting.has(key)) managedExisting.set(key, n);
      else n.remove();
    });

    const presentKeys = new Set(nonManagedKeys);
    managedExisting.forEach((_, key) => presentKeys.add(key));

    const pending = [];
    const addStyle = (el) => {
      const key = buildStyleKey(el);
      if (!key) return;

      if (presentKeys.has(key)) {
        managedExisting.delete(key);
        return;
      }

      const clone = el.cloneNode(true);
      clone.setAttribute(managedStyleAttr, "1");
      if (clone.tagName === "LINK") clone.setAttribute("fetchpriority", "high");

      const anchor = document.head.querySelector("link[rel='stylesheet'], style");
      if (anchor) document.head.insertBefore(clone, anchor);
      else document.head.appendChild(clone);

      presentKeys.add(key);
      if (clone.tagName === "LINK") pending.push(waitForStylesheet(clone));
    };

    doc.querySelectorAll("link[rel='stylesheet'],style").forEach(addStyle);
    managedExisting.forEach((n) => n.remove());

    try {
      const nonManagedScriptKeys = new Set();
      document.head
        .querySelectorAll(`script[data-wv-exec]:not([${managedScriptAttr}])`)
        .forEach((s) => {
          const key = buildScriptKey(s);
          if (key) nonManagedScriptKeys.add(key);
        });

      const existingScripts = new Map();
      document.head.querySelectorAll(`script[${managedScriptAttr}]`).forEach((s) => {
        const key = buildScriptKey(s);
        if (!key) return;
        if (nonManagedScriptKeys.has(key)) {
          s.remove();
          return;
        }
        if (!existingScripts.has(key)) existingScripts.set(key, s);
        else s.remove();
      });

      const presentScriptKeys = new Set(nonManagedScriptKeys);
      existingScripts.forEach((_, key) => presentScriptKeys.add(key));

      doc.querySelectorAll("head script").forEach((old) => {
        if (!old.hasAttribute("data-wv-exec")) return;
        const key = buildScriptKey(old);
        if (!key || presentScriptKeys.has(key)) {
          existingScripts.delete(key);
          return;
        }

        const s = document.createElement("script");
        copyAllAttributes(old, s);
        if (!old.defer) s.defer = true;
        s.setAttribute(managedScriptAttr, "1");
        if (!old.src) s.textContent = old.textContent || "";

        const anchorScript = document.head.querySelector("script");
        if (anchorScript) document.head.insertBefore(s, anchorScript);
        else document.head.appendChild(s);

        presentScriptKeys.add(key);
      });
    } catch {}

    if (pending.length) await Promise.allSettled(pending);
  };

  let mainEl = document.querySelector("main") || document.body;
  let activeNav = null;
  let backgroundController = null;
  let navFetchController = null;

  refreshCacheTargets();
  purgeLegacyCache();
  pruneCacheStorage();

  const isExecutableScriptType = (type) => {
    const t = String(type || "").trim().toLowerCase();
    return (
      !t ||
      t === "text/javascript" ||
      t === "application/javascript" ||
      t === "text/ecmascript" ||
      t === "application/ecmascript" ||
      t === "module"
    );
  };

  const executeInlineScripts = (root) => {
    try {
      root.querySelectorAll("script").forEach((old) => {
        if (!isExecutableScriptType(old.type)) return;
        if (old.src && !old.hasAttribute("data-wv-exec")) return;

        const s = document.createElement("script");
        copyAllAttributes(old, s);
        if (old.src && old.hasAttribute("data-wv-exec")) s.src = old.src;
        if (!old.src) s.textContent = old.textContent || "";
        old.replaceWith(s);
      });
    } catch {}
  };

  const notifyPageRehydrated = () => {
    activeNav = null;
    releaseFirstPaintGuard();

    window.dispatchEvent(new Event("meama:pageRehydrated"));

    try {
      document.dispatchEvent(new CustomEvent("page:loaded"));
      document.dispatchEvent(new Event("DOMContentLoaded"));

      try {
        const sections = mainEl.querySelectorAll(
          'section[data-section-id], [data-section-id].shopify-section, [data-section-type]'
        );
        sections.forEach((el) => {
          el.dispatchEvent(new Event("shopify:section:load", { bubbles: true }));
          el.dispatchEvent(new Event("section:load", { bubbles: true }));
        });
      } catch {}

      window.dispatchEvent(new Event("resize"));
      window.dispatchEvent(new Event("scroll"));
    } catch {}
  };

  const swapContent = async (html, newUrl, push = true) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const sourceMain = doc.querySelector("main") || doc.body;
    if (!sourceMain) return;

    await syncPageStyles(doc);

    const nextMain = sourceMain.cloneNode(true);
    document.title = doc.title || document.title;
    mainEl.replaceWith(nextMain);
    mainEl = nextMain;

    executeInlineScripts(mainEl);

    refreshCacheTargets();
    purgeLegacyCache();
    pruneCacheStorage();

    if (push) history.pushState({}, "", newUrl);

    if (push) {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {
        try {
          window.scrollTo(0, 0);
        } catch {}
      }
    }

    notifyPageRehydrated();
  };

  const updateCache = async (input) => {
    const descriptor = normalizeUrl(input);
    if (!descriptor) return;
    if (isHardReloadPath(descriptor.pathname)) return;
    if (!allowedCacheTargets.has(descriptor.originPath)) return;

    const key = getCacheKey(descriptor.absolute);
    const controller = new AbortController();
    if (backgroundController) backgroundController.abort();
    backgroundController = controller;

    try {
      const res = await fetch(descriptor.url.href, {
        cache: "no-store",
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!res.ok) return;
      if (!res.headers.get("content-type")?.includes("text/html")) return;

      const html = await res.text();
      const snapshot = createCacheSnapshot(html) || html;
      const finalDesc = normalizeUrl(res.url || descriptor.url.href) || descriptor;
      const finalKey = getCacheKey(finalDesc.absolute);

      writeCachedSnapshot(finalKey, snapshot);
      if (finalKey !== key) writeCachedSnapshot(key, snapshot);

      return snapshot;
    } catch {
      return null;
    } finally {
      if (backgroundController === controller) backgroundController = null;
    }
  };

  const sequentialPrecache = async (links) => {
    for (const a of links) {
      if (activeNav) return;
      const href = a.getAttribute("href");
      const n = normalizeUrl(href);
      if (!n || isHardReloadPath(n.pathname) || !allowedCacheTargets.has(n.originPath)) {
        continue;
      }

      await new Promise((resolve) =>
        runIdle(async () => {
          if (activeNav) return resolve();
          await updateCache(n.absolute);
          resolve();
        }, 500)
      );
    }
  };

  const maybeWarmPrefetch = (href) => {
    const d = normalizeUrl(href);
    if (!d) return;
    if (isSensitiveFlowPath(d.pathname) || isHardReloadPath(d.pathname)) return;
    if (!allowedCacheTargets.has(d.originPath)) return;

    const lastSeen = warmPrefetchSeen.get(d.originPath) || 0;
    const now = Date.now();
    if (now - lastSeen < 3000) return;
    warmPrefetchSeen.set(d.originPath, now);

    runIdle(() => updateCache(d.absolute), 80);
  };

  window.addEventListener("load", () => {
    runIdle(async () => {
      refreshCacheTargets();
      purgeLegacyCache();
      pruneCacheStorage();

      try {
        await updateCache(getLocalizedRoute("/collections/all-products"));
        await updateCache(getLocalizedRoute("/collections/offers"));
      } catch {}

      sequentialPrecache(dockLinks);
    }, 150);

    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 600);
  });

  document.body.addEventListener(
    "touchstart",
    (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      maybeWarmPrefetch(a.getAttribute("href"));
    },
    { passive: true }
  );

  document.body.addEventListener(
    "pointerdown",
    (e) => {
      if (typeof e.button === "number" && e.button !== 0) return;
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      maybeWarmPrefetch(a.getAttribute("href"));
    },
    { passive: true }
  );

  document.body.addEventListener("click", (e) => {
    if (e.defaultPrevented) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
    if (a.hasAttribute("download")) return;

    const target = a.getAttribute("target");
    if (target && target.toLowerCase() !== "_self") return;
    if (a.hasAttribute("data-no-instant") || a.hasAttribute("data-no-wv")) return;

    const descriptor = normalizeUrl(href);
    if (!descriptor) return;

    const samePath = normalizePathname(descriptor.pathname) === normalizePathname(location.pathname);
    const sameQuery = (descriptor.search || "") === (location.search || "");
    if (samePath && sameQuery) return;

    if (isSensitiveFlowPath(descriptor.pathname)) return;
    if (isHardReloadPath(descriptor.pathname) || !allowedCacheTargets.has(descriptor.originPath)) return;

    e.preventDefault();
    activeNav = descriptor.absolute;

    if (backgroundController) backgroundController.abort();
    if (navFetchController) navFetchController.abort();

    const key = getCacheKey(descriptor.absolute);
    const cached = readCachedSnapshot(key);

    if (cached) {
      swapContent(cached, descriptor.url.href).then(() =>
        runIdle(() => updateCache(descriptor.absolute), 200)
      );
      return;
    }

    const controller = new AbortController();
    navFetchController = controller;

    fetch(descriptor.absolute, {
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("Network");
        const html = await r.text();
        return { html, final: normalizeUrl(r.url) || descriptor };
      })
      .then(({ html, final }) => {
        if (controller !== navFetchController || activeNav !== descriptor.absolute) return;

        const snapshot = createCacheSnapshot(html) || html;
        const finalKey = getCacheKey(final.absolute);
        writeCachedSnapshot(finalKey, snapshot);
        if (finalKey !== key) writeCachedSnapshot(key, snapshot);

        return swapContent(snapshot, final.url.href);
      })
      .catch(() => {
        window.location.href = descriptor.url.href;
      })
      .finally(() => {
        if (controller === navFetchController) navFetchController = null;
      });
  });

  window.addEventListener("popstate", () => {
    const d = normalizeUrl(location.href);
    if (!d) return;
    if (isHardReloadPath(d.pathname) || !allowedCacheTargets.has(d.originPath)) return;

    let cached = readCachedSnapshot(getCacheKey(d.absolute));
    if (!cached) cached = readCachedSnapshot(getCacheKey(d.originPath));
    if (cached) {
      swapContent(cached, d.url.href, false);
      return;
    }

    runIdle(() => updateCache(d.absolute), 50);
  });

  window.addEventListener("pageshow", (e) => {
    if (e.persisted) {
      releaseFirstPaintGuard();
      refreshCacheTargets();
      purgeLegacyCache();
      pruneCacheStorage();
    }
  });

  window.addEventListener("meama:pageRehydrated", () => {
    runIdle(() => {
      if (typeof window.initMeamaMap === "function") window.initMeamaMap();
      if (typeof window.initCarousels === "function") window.initCarousels();
    }, 200);
    runIdle(() => sequentialPrecache(dockLinks), 600);
  });

  window.addEventListener("pagehide", () => {
    try {
      const d = normalizeUrl(location.href);
      if (!d || !allowedCacheTargets.has(d.originPath)) return;
      const snapshot =
        createCacheSnapshot(document.documentElement.outerHTML) ||
        document.documentElement.outerHTML;
      writeCachedSnapshot(getCacheKey(d.absolute), snapshot);
    } catch {}
  });
})();
